import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { getCoreRowModel } from '@tanstack/react-table';

// Theme MUI
import Paper from '@mui/material/Paper';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';

import { useStandardTable as useTable, Table } from '../../common/table/index';

// Hooks
import { useAppDispatch, useAppSelector } from '../../../redux/hook';

// Utils
import { Storage } from '../../../utils/Storage';
import { makeTasksQueueColumn } from '../../../utils/columns/makeTasksQueueColumn';
import { FormatForApi, shortDateFormat } from '../../../utils/Format';

// Redux
import { fetchTasksQueue } from '../../../redux/slices/tasksQueueSlice';
import { fetchAllSettings } from '../../../redux/slices/settingsSlice';

// Services
import { getTaskQueueChangeLog, getTaskQueues } from '../../../services/tasksQueue.service';

// Constants
import { SearchBarFilterTypes, TasksReaderQueue } from '../../../constants/Constants';
import { PaginatedRequest } from '../../../requestModels/paginatedRequest';
import { FilterableTypes } from '../../../types/FilterableTypes';

// Common
import SearchBar from '../../common/SearchBar/SearchBar';
import ChangeLogList from '../../common/ChangeLog';
import CustomModal from '../../common/withModal';
import { Spinner } from '../../common/Spinner';
import FormInputCollection from '../../common/FormInputCollection/FormInputCollection';

// Components
import TasksQueueStatusTabs from './components/tasksQueueStatusTabs';
import TasksQueueAssignedTo from './components/tasksQueueAssignedTo';
import TasksQueueDetails from './components/tasksQueueDetails';
import TasksQueueSeverityTabs from './components/tasksQueueSeverityTabs';

const TasksQueue = () => {
  const [lastSeenDateTimeFrom, setLastSeenDateTimeFrom] = useState<Date | null>(null);
  const [lastSeenDateTimeTo, setLastSeenDateTimeTo] = useState<Date | null>(null);
  const [changeLogTargetId, setChangeLogTargetId] = useState<string | undefined>(undefined);
  const [changedTaskDetail, setChangedTaskDetail] = useState<any>();
  const [paramFilter, setParamFilter] = useState<any>({});
  const [taskStatusToggles, setTaskStatusToggles] = useState<number[]>([]);
  const [taskSeverityToggles, setTaskSeverityToggles] = useState<number[]>([]);
  const [resetAssignedToSelection, setResetAssignedToSelection] = useState(false);
  const [hasFilterChanged, setHasFiterChanged] = useState<any>(null);
  const [isResetting, setIsResetting] = useState(false);

  const forceReload = () => setParamFilter({ ...paramFilter, changedAt: new Date() });

  const dispatch = useAppDispatch();
  const tasksQueueState = useAppSelector((state) => state.tasksQueue);
  const settingStates = useAppSelector((state) => state.settings);

  const defaultFilters = () => {
    const filterState = Storage.getSearchBarFiltering(SearchBarFilterTypes.TASKSQUEUE);
    if (filterState) {
      setParamFilter({
        filters: filterState.filters,
        sortBy: filterState.sortBy,
      });
    }
  };

  useEffect(() => {
    dispatch(fetchAllSettings());
  }, []);

  useEffect(() => {
    if (!tasksQueueState.tasks && tasksQueueState.areTasksLoading) {
      dispatch(fetchTasksQueue());
      defaultFilters();
    }
  }, [tasksQueueState.areTasksLoading, tasksQueueState.tasks]);

  useEffect(() => {
    if (isResetting) {
      // Reset paramFilter and clear the resetting flag
      setParamFilter({ filters: [], sortBy: paramFilter.sortBy });
      setIsResetting(false);
    }
  }, [isResetting, paramFilter.sortBy]);

  useEffect(() => {
    if (hasFilterChanged && paramFilter) {
      setParamFilter({ ...paramFilter, changedAt: new Date() });
    }
  }, [hasFilterChanged]);

  const handleReset = () => {
    setIsResetting(true);
    setLastSeenDateTimeFrom(null);
    setLastSeenDateTimeTo(null);
    setTaskStatusToggles([]);
    setTaskSeverityToggles([]);
    setParamFilter({ filters: [], sortBy: paramFilter.sortBy });
    setResetAssignedToSelection((prev) => !prev);
    Storage.setSearchBarFiltering(SearchBarFilterTypes.TASKSQUEUE, { filters: [] });
  };

  const getChangeLog = async (request: PaginatedRequest): Promise<any> => {
    if (changeLogTargetId) return await getTaskQueueChangeLog(changeLogTargetId, request);
    return null;
  };

  const handleOpenChangeLog = (id: string): void => {
    setChangeLogTargetId(id);
  };

  const handleOpenTasksDetail = (taskDetail: any): void => {
    setChangedTaskDetail(taskDetail);
  };

  const handleWatchersUpdate = () => {
    forceReload();
  };

  const handleStatusChanged = () => {
    setHasFiterChanged({ changedAt: new Date() });
  };

  const columns = useMemo(
    () => makeTasksQueueColumn(handleOpenChangeLog, handleOpenTasksDetail, handleStatusChanged, settingStates),
    [settingStates, hasFilterChanged],
  );

  const table = useTable<any>({
    columns,
    fetchAsync: getTaskQueues,
    params: paramFilter,
    serverSide: true,
    shouldFetch: true,
  });

  const onSearchChange = (newFilters: any[]) => {
    // Create a map to manage existing filters
    const filterMap = new Map();

    // Add existing filters to the map
    (paramFilter.filters || []).forEach((filter: any) => {
      if (filter.category) {
        filterMap.set(TasksReaderQueue.Category, filter);
      } else if (filter.status) {
        filterMap.set(TasksReaderQueue.Status, filter);
      } else if (filter.severity) {
        filterMap.set(TasksReaderQueue.Severity, filter);
      } else {
        filterMap.set(JSON.stringify(filter), filter); // Use JSON.stringify for complex objects
      }
    });

    // Add new filters, ensuring that category and other filters are preserved
    newFilters.forEach((newFilter) => {
      if (newFilter.category) {
        filterMap.set(TasksReaderQueue.Category, {
          ...newFilter,
          filterType: FilterableTypes.Contains,
        });
      } else if (newFilter.status) {
        filterMap.set(TasksReaderQueue.Status, {
          ...newFilter,
          filterType: FilterableTypes.Contains,
        });
      } else if (newFilter.severity) {
        filterMap.set(TasksReaderQueue.Severity, {
          ...newFilter,
          filterType: FilterableTypes.Contains,
        });
      } else {
        filterMap.set(JSON.stringify(newFilter), newFilter);
      }
    });

    // Convert map back to an array, but exclude category if the newFilters array is empty
    const updatedFilters = Array.from(filterMap.values()).filter((filter) => {
      // Exclude category filter if it's being cleared
      if (newFilters.length === 0 && TasksReaderQueue.Category in filter) {
        return false;
      }
      return true;
    });

    // Add date filters
    if (lastSeenDateTimeFrom && lastSeenDateTimeTo) {
      updatedFilters.push({
        createdDateTime: FormatForApi(moment(lastSeenDateTimeFrom), true),
        filterType: FilterableTypes.GreaterThanOrEqual,
        and: {
          createdDateTime: FormatForApi(moment(lastSeenDateTimeTo), false),
          filterType: FilterableTypes.LessThanOrEqual,
        },
      });
    }

    // Update state and storage
    setParamFilter({ filters: updatedFilters, sortBy: paramFilter.sortBy });
    Storage.setSearchBarFiltering(SearchBarFilterTypes.TASKSQUEUE, { isMoreFilter: true, filters: updatedFilters });
  };

  const handleFilterToggle = (type: 'status' | 'assignedUserName' | 'severity', selectedValues: string[] | number[]) => {
    const filterValue = selectedValues.join('|');

    // Filter out the existing filters of the same type
    const updatedFilters = (paramFilter.filters || []).filter((filter: any) => {
      if (type === TasksReaderQueue.Status) {
        return !(TasksReaderQueue.Status in filter);
      } else if (type === TasksReaderQueue.AssignedUserName) {
        return !(TasksReaderQueue.AssignedUserName in filter);
      } else if (type === TasksReaderQueue.Severity) {
        return !(TasksReaderQueue.Severity in filter);
      }
      return true;
    });

    // Add new filters only if there are selected values
    const newFilters = filterValue ? [{ [type]: filterValue, filterType: FilterableTypes.Contains }] : [];

    // Update toggles and reapply filters
    if (type === TasksReaderQueue.Status) {
      setTaskStatusToggles(selectedValues as number[]);
    } else if (type === TasksReaderQueue.Severity) {
      setTaskSeverityToggles(selectedValues as number[]);
    }

    // Combine updated and new filters
    const combinedFilters = [...updatedFilters, ...newFilters];

    // Update paramFilter with the new set of filters
    setParamFilter({ filters: combinedFilters, sortBy: paramFilter.sortBy });
    Storage.setSearchBarFiltering(SearchBarFilterTypes.TASKSQUEUE, {
      isMoreFilter: true,
      filters: combinedFilters,
    });
  };

  return (
    <>
      <CustomModal title='ChangeLog' isOpen={!!changeLogTargetId} setIsOpen={() => setChangeLogTargetId(undefined)} maxWidth='lg'>
        <ChangeLogList fetchAsync={getChangeLog} />
      </CustomModal>
      <SearchBar
        pageKey={SearchBarFilterTypes.TASKSQUEUE}
        onSearch={onSearchChange}
        onReset={handleReset}
        columns={columns}
        allowSearchAll={true}
        components={
          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            <TasksQueueStatusTabs
              taskStatusToggles={taskStatusToggles}
              settingStates={settingStates}
              handleTaskStatusToggle={(event, newValues) => handleFilterToggle('status', newValues)}
            />
            <TasksQueueSeverityTabs
              taskSeverityToggles={taskSeverityToggles}
              settingStates={settingStates}
              handleTaskSeverityToggle={(event, newValues) => handleFilterToggle('severity', newValues)}
            />
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <FormInputCollection label='Created Date' style={{ marginLeft: '24px' }}>
                <div style={{ marginRight: '8px' }}>
                  <DatePicker
                    label='From'
                    value={lastSeenDateTimeFrom ? moment(lastSeenDateTimeFrom) : null}
                    format={shortDateFormat}
                    sx={{ width: '185px' }}
                    slotProps={{ textField: { size: 'small' } }}
                    onChange={(date) => {
                      setLastSeenDateTimeFrom(date ? date.toDate() : null);
                    }}
                  />
                </div>
                <DatePicker
                  label='To'
                  value={lastSeenDateTimeTo ? moment(lastSeenDateTimeTo) : null}
                  format={shortDateFormat}
                  sx={{ width: '185px' }}
                  slotProps={{ textField: { size: 'small', style: { fontSize: 10 } } }}
                  onChange={(date) => {
                    setLastSeenDateTimeTo(date ? date.toDate() : null);
                  }}
                />
              </FormInputCollection>
            </LocalizationProvider>
            <TasksQueueAssignedTo
              resetAssignedToSelection={resetAssignedToSelection}
              handleAssignedUserToggle={(selectedLabels) => handleFilterToggle('assignedUserName', selectedLabels)}
            />
          </div>
        }
      />
      <CustomModal isOpen={!!changedTaskDetail} setIsOpen={handleOpenTasksDetail} showSaveButton={false} maxWidth='md'>
        {changedTaskDetail && (
          <TasksQueueDetails selectedRowData={changedTaskDetail} settingStates={settingStates} onWatchersUpdate={handleWatchersUpdate} />
        )}
      </CustomModal>

      {tasksQueueState.areTasksLoading && <Spinner loading={tasksQueueState.areTasksLoading ?? false} />}
      <Paper>
        <Table {...table} showSearchBar={false} showColumnToggler={false} getCoreRowModel={getCoreRowModel()} />
      </Paper>
    </>
  );
};

export default TasksQueue;
